// Rendered commentary markdown. The renderer has raw HTML disabled, so what it
// returns is safe to insert; see shared/src/markdown.ts.
//
// Glossary words are marked in running prose only: not in a one-line heading
// or title, and not where `plain` says the text is reference matter — a
// citation is not something anyone reads for its vocabulary.
//
// Running prose also draws its charts. A quotation stays a link: the chapter
// page already holds its chapter of the ESV, and a note is not a way in for
// more.
import { renderMarkdown, renderMarkdownInline, renderParts } from "@bibleunleashed/shared";
import { useMemo } from "react";
import { BadChart, Chart } from "@/charts/Chart";
import { useTermMatcher } from "@/lib/glossary";

export function Prose({ md, inline, plain, className = "" }: { md: string; inline?: boolean; plain?: boolean; className?: string }) {
  const terms = useTermMatcher();
  const parts = useMemo(
    () => (inline || plain ? null : renderParts(md, terms, { quotes: false })),
    [md, inline, plain, terms],
  );

  if (!parts) {
    const html = inline ? renderMarkdownInline(md) : renderMarkdown(md, null);
    return <div className={`${inline ? "" : "prose"} ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  // Nearly always: prose and nothing else, as one block.
  if (parts.every((p) => p.kind === "prose")) {
    const html = parts.map((p) => (p.kind === "prose" ? p.html : "")).join("");
    return <div className={`prose ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <div className={className}>
      {parts.map((part, i) =>
        part.kind === "prose" ? (
          <div key={i} className="prose" dangerouslySetInnerHTML={{ __html: part.html }} />
        ) : part.kind === "chart" ? (
          <Chart key={i} chart={part.chart} />
        ) : part.kind === "bad-chart" ? (
          <BadChart key={i} error={part.error} />
        ) : null,
      )}
    </div>
  );
}
